import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const ModalImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 95vw;
  max-height: 95vh;
  position: relative;
`;

const BlurredPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    var(--accent-light) 0%,
    rgba(var(--accent-rgb), 0.3) 50%,
    var(--accent-light) 100%
  );
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
  filter: blur(20px);
  border-radius: 15px;
`;

const ModalImage = styled.img`
  max-width: 95vw;
  max-height: 95vh;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 15px;
  opacity: ${(props) => (props.$loaded ? 1 : 0)};
  transition: opacity 0.6s ease;
  animation: ${(props) => (props.$loaded ? fadeIn : "none")} 0.6s ease;
  box-shadow: var(--shadow-medium);
`;

const LazyModalImage = ({ src, alt, ...props }) => {
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (src) {
      setLoading(true);
      setLoaded(false);

      const img = new Image();
      img.onload = () => {
        setDimensions({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
        setLoaded(true);
        setLoading(false);
      };
      img.onerror = () => {
        setLoading(false);
      };
      img.src = src;
    }
  }, [src]);

  // Calculate placeholder dimensions
  const getPlaceholderSize = () => {
    if (dimensions.width && dimensions.height) {
      const maxWidth = window.innerWidth * 0.95;
      const maxHeight = window.innerHeight * 0.95;

      const aspectRatio = dimensions.width / dimensions.height;

      let width = dimensions.width;
      let height = dimensions.height;

      if (width > maxWidth) {
        width = maxWidth;
        height = width / aspectRatio;
      }

      if (height > maxHeight) {
        height = maxHeight;
        width = height * aspectRatio;
      }

      return { width, height };
    }

    // Default placeholder size
    return { width: 300, height: 200 };
  };

  const placeholderSize = getPlaceholderSize();

  return (
    <ModalImageContainer {...props}>
      {!loaded && (
        <BlurredPlaceholder
          style={{
            width: placeholderSize.width,
            height: placeholderSize.height,
          }}
        />
      )}
      {loading && (
        <l-line-spinner size="24" stroke="2" speed="1" color="var(--accent)" />
      )}
      <ModalImage
        src={src}
        alt={alt}
        $loaded={loaded}
        onLoad={() => {
          setLoaded(true);
          setLoading(false);
        }}
        onError={() => {
          setLoading(false);
        }}
      />
    </ModalImageContainer>
  );
};

export default LazyModalImage;
